import {
  Box,
  Divider,
  Dropdown,
  Grid,
  MixpanelEventSource,
  MixpanelSourceProvider,
  Tour,
  type DropdownProps,
} from '@talos/kyoko';
import type { ReactNode } from 'react';
import { TourName } from '../../tokens/tours';
import { MarketSelectorBlotter } from './blotter/MarketSelectorBlotter';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPresetsList } from './presets/MarketSelectorPresetsList';
import { MARKET_SELECTOR_AGGREGATIONS_WIDTH } from './tokens';

export type MarketSelectorPanelProps = {
  dropdownProps: Omit<DropdownProps, 'children'>;
  /** Useful if you have multiple market selectors rendered at once to give them different testIDs. */
  testID?: string;
  /** Pass a react node here to render above the aggregations and blotter boxes */
  panelTopPrefix?: ReactNode;
  /** Pass a react node here to render below the aggregations and blotter boxes */
  panelBottomSuffix?: ReactNode;
  /**
   * Forces the panel content to remount when the backing selector context changes.
   *
   * Use this when a single panel instance stays open while swapping to a meaningfully different
   * item universe, enrichment set, or value owner. This keeps transient child state in the blotter,
   * preset list, and custom panel prefix from leaking across those contexts.
   */
  panelContentKey?: string;
};

export const MarketSelectorPanel = ({
  dropdownProps,
  testID,
  panelTopPrefix,
  panelBottomSuffix,
  panelContentKey,
}: MarketSelectorPanelProps) => {
  const { disableAggregations } = useMarketSelectorContext();

  return (
    <Dropdown portalize isParentDropdown {...dropdownProps}>
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        {/* Tour for advanced features - only show when modal is open */}
        {dropdownProps.isOpen && !disableAggregations && <Tour tour={TourName.v70MarketSelectorDefaultAggregation} />}
        {dropdownProps.isOpen && <Tour tour={TourName.v253MarketSelectorAdvancedFeatures} />}

        <Box
          key={panelContentKey}
          flex="1"
          display="flex"
          flexDirection="column"
          position="relative"
          data-testid={`${testID ?? 'market-selector-panel'}`}
        >
          {panelTopPrefix ? (
            <Box flex="0 0 auto" borderBottom="1px solid" borderColor="backgroundDivider">
              {panelTopPrefix}
            </Box>
          ) : null}
          <Grid
            flex={1}
            gridTemplateColumns={
              disableAggregations ? 'auto' : `${MARKET_SELECTOR_AGGREGATIONS_WIDTH}px min-content auto`
            }
          >
            {!disableAggregations && (
              <>
                <MarketSelectorPresetsList />
                <Divider orientation="vertical" my="0" color="backgroundDivider" />
              </>
            )}
            <MarketSelectorBlotter />
          </Grid>
          {panelBottomSuffix ? (
            <Box flex="0 0 auto" borderTop="1px solid" borderColor="backgroundDivider">
              {panelBottomSuffix}
            </Box>
          ) : null}
        </Box>
      </MixpanelSourceProvider>
    </Dropdown>
  );
};
